/* eslint-disable no-unused-vars */
"use client";

import React, { forwardRef, useCallback } from "react";

import { Icon } from "@cobasi/ds-icons";
import SearchIcon from "@cobasi/ds-icons/regular/search";
import XmarkIcon from "@cobasi/ds-icons/regular/xmark";

import { useSearch } from "@/hooks";

import type { SearchHandle, SearchProps } from "./search.types";

import s from "./search.module.scss";

const Search = <T,>(
  {
    label,
    placeholder,
    items,
    setItems,
    getLabel,
    onSearchSubmit,
    size = "md",
    type = "text",
    helperText = "",
    iconPosition = "right",
    variant = "turquoise",
    disabled = false,
    fullWidth = false,
    error = false,
    ...rest
  }: SearchProps<T>,
  ref: React.Ref<SearchHandle>
) => {
  const {
    value,
    suggestions,
    selectedIndex,
    inputRef,
    suggestionContainerRef,
    setValue,
    handleOnFocus,
    handleKeyDown,
    setSuggestions,
    handleClearInput,
    handleInputChange,
    handleClickSuggestion
  } = useSearch<T>(
    {
      items,
      getLabel,
      onSearchSubmit
    },
    ref
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!value.trim()) return;

      if (onSearchSubmit) {
        onSearchSubmit(value);
      }

      inputRef.current?.blur();
    },
    [value, inputRef, onSearchSubmit]
  );

  return (
    <div className={s.search}>
      <form onSubmit={handleSubmit} data-testid="search-form">
        <div className={`input-container ${fullWidth ? "full-width" : ""}`}>
          <div
            className={`input-field ${error ? "error" : ""} input-field-${size} input-field-${variant} ${disabled ? "disabled" : ""}`}
          >
            {iconPosition === "left" && (
              <Icon
                className="icon"
                icon={SearchIcon}
                data-testid="left-icon"
              />
            )}
            <input
              {...rest}
              ref={inputRef}
              value={value}
              onChange={(e) => {
                handleInputChange(e);
              }}
              onFocus={handleOnFocus}
              placeholder={placeholder}
              type={type}
              disabled={disabled}
              onKeyDown={(e) =>
                handleKeyDown(
                  e,
                  () => {
                    if (selectedIndex !== null) {
                      handleClickSuggestion(
                        suggestions[selectedIndex],
                        setItems
                      );
                    }
                  },
                  suggestionContainerRef
                )
              }
            />
            <label htmlFor="input">{label}</label>
            {iconPosition === "right" && (
              <>
                <button
                  type="button"
                  className={s["button-clear-search"]}
                  data-value-search={value}
                  aria-label="Limpar busca"
                  onKeyDown={(e) => e.key === "Enter" && handleClearInput()}
                  onClick={() => handleClearInput()}
                  style={value ? { display: "block" } : { display: "none" }}
                >
                  <Icon icon={XmarkIcon} height="1.2rem" width="1.2rem" />
                </button>
                <button
                  className={s["button-submit"]}
                  type="submit"
                  disabled={!value.trim()}
                >
                  <Icon
                    className="icon"
                    icon={SearchIcon}
                    data-testid="right-icon"
                  />
                </button>
              </>
            )}
          </div>
          {!!helperText && <div className="helper-text">{helperText}</div>}
        </div>
        {value.trim() !== "" && suggestions.length > 0 && (
          <ul
            ref={suggestionContainerRef}
            className={s["suggestion-container"]}
            data-is-open={suggestions.length > 0}
            role="rolelistbox"
          >
            {suggestions.map((suggestion, index) => (
              <li
                className={`body-text-md ${selectedIndex === index ? s.selected : ""}`}
                key={index}
                onClick={() => {
                  if (onSearchSubmit) {
                    setValue(suggestion);
                    setSuggestions([]);
                    onSearchSubmit(suggestion);
                  } else {
                    handleClickSuggestion(suggestion, setItems);
                  }
                  inputRef.current?.blur();
                }}
                role="option"
                aria-selected={selectedIndex === index}
              >
                <Icon
                  icon={SearchIcon}
                  width="1.2rem"
                  height="1.2rem"
                  className="icon"
                />
                {suggestion}
              </li>
            ))}
          </ul>
        )}
      </form>
    </div>
  );
};

export default forwardRef(Search) as <T>(
  props: SearchProps<T> & { ref?: React.Ref<SearchHandle> }
) => React.JSX.Element;
